import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useApi } from "@/api/hooks/useApi";
import { useProjectId } from "@/hooks/Network/useProjectId";
import { QUERY_KEYS } from "@/query-keys";
import type { ApiResponseType } from "@/types/api-types";

const useUpdateFahSuggestionRead = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  const projectId = useProjectId();

  return useMutation({
    mutationFn: (suggestionId: string) => api.postAnalyticsFeelingAtHomeSuggestionsReadV1(suggestionId),
    onMutate: (suggestionId) => {
      queryClient.setQueryData<ApiResponseType<"getAnalyticsFeelingAtHomeSuggestionsV1">>(
        QUERY_KEYS.ANALYTICS_FEELING_AT_HOME_SUGGESTIONS(projectId),
        (currData) => {
          if (!currData) {
            return;
          }

          const currSuggestions = currData.data.suggestions;
          const updatedSuggestions = currSuggestions?.map((suggestion) => {
            if (suggestion.id === suggestionId) {
              // This is the clilent-side date, which is not 100% matching with
              // server-side date, but it's sufficient for optimistic update
              return { ...suggestion, viewedAt: new Date().toISOString() };
            }

            return suggestion;
          });

          return {
            ...currData,
            data: {
              suggestions: updatedSuggestions,
              lastUpdatedAt: currData.data.lastUpdatedAt,
            },
          };
        },
      );
    },
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.ANALYTICS_FEELING_AT_HOME_SUGGESTIONS(projectId),
      });
    },
  });
};

const useAddFahSuggestionTipRating = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  const projectId = useProjectId();

  return useMutation({
    mutationFn: ({ tipId, data }: { tipId: string; data: "positive" | "negative" }) =>
      api.postAnalyticsFeelingAtHomeSuggestionsTipsRateV1(tipId, data),
    onMutate: (payload) => {
      queryClient.setQueryData<ApiResponseType<"getAnalyticsFeelingAtHomeSuggestionsV1">>(
        QUERY_KEYS.ANALYTICS_FEELING_AT_HOME_SUGGESTIONS(projectId),
        (currData) => {
          if (!currData) {
            return;
          }

          const currSuggestions = currData.data.suggestions;
          const updatedSuggestions = currSuggestions?.map((suggestion) => {
            const updatedTipIndex = suggestion.tips.findIndex((tip) => tip.id === payload.tipId);
            if (updatedTipIndex === -1) {
              return suggestion;
            }

            const updatedTip = {
              ...suggestion.tips[updatedTipIndex],
              rating: payload.data,
              // This is the client-side date, which is not 100% matching with
              // server-side date, but it's sufficient for optimistic update
              ratedAt: new Date().toISOString(),
            };
            const updatedTips = [...suggestion.tips];
            updatedTips[updatedTipIndex] = updatedTip;

            return { ...suggestion, tips: updatedTips };
          });

          return {
            ...currData,
            data: {
              suggestions: updatedSuggestions,
              lastUpdatedAt: currData.data.lastUpdatedAt,
            },
          };
        },
      );
    },
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.ANALYTICS_FEELING_AT_HOME_SUGGESTIONS(projectId),
      });
    },
  });
};

export const analyticsMutations = {
  useUpdateFahSuggestionRead,
  useAddFahSuggestionTipRating,
};
